import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, MenuItem, RadioGroup, FormControlLabel, Radio, TextField } from '@mui/material';
import { UpdateApplicationV2Dto, ApplicationDesiredLeaseTerm } from '@inosio/payments-sdk';
import { applicationStyles } from './CustomStyles';
import { InputField } from '../../../components/InputController';
import CustomDropdown from '../../../components/customDropdown';
import * as Yup from 'yup';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { useFormik } from 'formik';
import { getApplicationV2, selectApplicantsState, updateApplicationV2 } from '../sagas/applicants/applicantsSlice';
import { convertSqlDateToUSCalendarDate, convertUSCalendarDateToSqlDate } from '../../../shared/util/converters';
import { InosioDateFormat } from '../../../shared/util/formaters';
import { useEffect, useState } from 'react';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { DobRegex } from '../../../utils/regex';
import { compareAsc, format } from 'date-fns';

const schemaValidationApplicationInfo = Yup.object({
  ShowingAgentName: Yup.string().required('Showing Agent Name is required'),
  ShowingAgentCompany: Yup.string().required('Showing Agent Company is required'),
  ShowingAgentContactDetails: Yup.string().required('Showing Agent Contact Details are required'),
  ExpectedRentAmount: Yup.string()
    .required()
    .matches(/^[1-9][0-9]*$/, 'Rent amount cannot be 0'),
  DesiredLeaseTerm: Yup.string().required(),
  DesiredMoveInDate: Yup.string()
    .min(10, 'Please enter a date in this format MM/DD/YYYY')
    .max(10, 'Please enter a date in this format MM/DD/YYYY')
    .matches(DobRegex, 'Please enter a date in this format MM/DD/YYYY')
    .test({
      name: 'Desired Move in Date',
      exclusive: true,
      message: 'Desired move-in date must be today or in the future.',
      test: (value, testContext) => {
        if (testContext.parent.DesiredMoveInDate) {
          const moveInDate = new Date(testContext.parent.DesiredMoveInDate);
          const today = new Date(format(new Date(), 'MM/dd/yyyy')); //remove time portion

          //return 1 if the first date is after the second, -1 if the first date is before the second or 0 if dates are equal.
          const isMoveInDateInFuture = compareAsc(moveInDate, today);
          return isMoveInDateInFuture > -1;
        }
        return false;
      },
    })
    .required('Please enter a move in date'),
  ApplicantMemo: Yup.string().max(1000, 'Max character limit reached.').nullable(),
});

type schemaValidationApplicationInfoType = Yup.InferType<typeof schemaValidationApplicationInfo>;

export interface PropertyInfoPropsType {
  appUuid: string;
}

export default function PropertyInformation(props: PropertyInfoPropsType) {
  const classes = applicationStyles();
  const query = useQuery();
  const hasValidationQueryParam = !!query.get('validation');
  const dispatch = useDispatch();

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const { Application, isFetchingApplicationFailure, isFetchingApplicationSuccess, isUpdatingApplicationFailed } = useSelector(selectApplicantsState);
  const { isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);

  //local states
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [hasSeenProperty, setHasSeenProperty] = useState<boolean | null>(null); //local state for keeping track of what to show in the radio for has seen property
  const [initialFormValues, setInitialFormValues] = useState<schemaValidationApplicationInfoType>({
    ApplicantMemo: '',
    ShowingAgentName: '',
    ShowingAgentCompany: '',
    ShowingAgentContactDetails: '',
    ExpectedRentAmount: '',
    DesiredLeaseTerm: ApplicationDesiredLeaseTerm.LessThan1Year,
    DesiredMoveInDate: '',
  });

  const formik = useFormik<schemaValidationApplicationInfoType>({
    initialValues: initialFormValues,
    validationSchema: schemaValidationApplicationInfo,

    onSubmit: async () => {},
    enableReinitialize: true,
    validateOnMount: true,
  });

  //# region useffects

  //step 1: on load grab fresh copy of application
  useEffect(() => {
    dispatch({
      type: getApplicationV2.type,
      payload: props.appUuid,
    });
  }, [dispatch]);

  //step 2: set the initial form values with the info you get from the Application fetch
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid && isFetchingApplicationSuccess) {
      setInitialFormValues({
        ...initialFormValues,
        ApplicantMemo: Application.Memo ?? '',
        ShowingAgentName: Application.ShowingAgentName ?? '',
        ShowingAgentCompany: Application.ShowingAgentCompany ?? '',
        ShowingAgentContactDetails: Application.ShowingAgentContactDetails ?? '',
        ExpectedRentAmount: Application.ExpectedRentAmount ? (Application.ExpectedRentAmount / 100).toString() : '',
        DesiredLeaseTerm: Application.DesiredLeaseTerm ?? '',
        DesiredMoveInDate: Application.DesiredMoveInDate ? convertSqlDateToUSCalendarDate(Application.DesiredMoveInDate) : '',
      });
    }
  }, [ipaUser, isFetchingApplicationSuccess]);

  //step 3: set the radio button values once you get application data
  useEffect(() => {
    if (isFetchingApplicationSuccess) {
      setHasSeenProperty(Application.HasSeenProperty ?? null);
    }
  }, [Application, isFetchingApplicationSuccess]);

  //error handling
  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingApplicationSuccess && isFetchingPropertyGroupDetailsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingApplicationFailure || isFetchingPropertyGroupDetailsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the property information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [isFetchingApplicationSuccess, isFetchingApplicationFailure, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed]);

  //show error if application update failed
  useEffect(() => {
    if (isUpdatingApplicationFailed) {
      setLocalError('There was an error saving your information. Try refreshing the page');
    }
  }, [isUpdatingApplicationFailed]);
  //#region end useffects

  //handlers
  const handleFormValuesChanged = (e: any) => {
    const payload: UpdateApplicationV2Dto = {
      ShowingAgentName: !formik.errors.ShowingAgentName ? formik.values.ShowingAgentName || undefined : undefined,
      ShowingAgentCompany: !formik.errors.ShowingAgentCompany ? formik.values.ShowingAgentCompany || undefined : undefined,
      ShowingAgentContactDetails: !formik.errors.ShowingAgentContactDetails ? formik.values.ShowingAgentContactDetails || undefined : undefined,
      DesiredLeaseTerm: !formik.errors.DesiredLeaseTerm ? toApplicationDesiredLeaseTerm(formik.values.DesiredLeaseTerm) : undefined,
      DesiredMoveInDate: !formik.errors.DesiredMoveInDate ? convertUSCalendarDateToSqlDate(formik.values.DesiredMoveInDate) : undefined,
      ExpectedRentAmount: !formik.errors.ExpectedRentAmount ? parseInt(formik.values.ExpectedRentAmount) * 100 : undefined,
      ApplicantMemo: !formik.errors.ApplicantMemo ? formik.values.ApplicantMemo || '' : undefined, //this is a special case where an empty value for memo is allowed
    };

    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch({
      type: updateApplicationV2.type,
      payload: {
        applicationUuid: Application.Uuid,
        updateDto: payload,
      },
    });
  };

  //handler for radio button for pets
  const handleUpdateHasSeenProperty = (e: any) => {
    const selectedHasSeenProperty = e.target.value === 'true';
    if (selectedHasSeenProperty) {
      dispatch({
        type: updateApplicationV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          updateDto: { HasSeenProperty: true },
        },
      });
      setHasSeenProperty(true);
    } else {
      dispatch({
        type: updateApplicationV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          updateDto: { HasSeenProperty: false, ShowingAgentName: '', ShowingAgentCompany: '', ShowingAgentContactDetails: '' },
        },
      });
      setHasSeenProperty(false);
    }
  };

  //formatters
  const formatDesiredMoveInDate = (desiredDate: string) => {
    const formattedDesiredMoveInDate = InosioDateFormat(desiredDate);
    formik.setFieldValue('DesiredMoveInDate', formattedDesiredMoveInDate);
  };

  // Function to convert string to desired lease term enum
  const toApplicationDesiredLeaseTerm = (value: string): ApplicationDesiredLeaseTerm | undefined => {
    switch (value) {
      case ApplicationDesiredLeaseTerm.LessThan1Year:
      case ApplicationDesiredLeaseTerm._1Year:
      case ApplicationDesiredLeaseTerm.MoreThan1Year:
        return value as ApplicationDesiredLeaseTerm;
      default:
        return undefined;
    }
  };

  return (
    <Box width="100%" minHeight="60vh">
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box width="100%" p={4} minHeight="inherit">
          <Grid container spacing={4}>
            {localError && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
                <Typography className={classes.globalText}>{localError}</Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6} lg={4.5}>
              <Typography sx={{ mb: 2 }} className={classes.labelText}>
                Property Details
              </Typography>
              <InputField name={'Property'} label={Application?.PropertyAddress ? '' : 'Property'} value={Application?.PropertyAddress} />
              <InputField
                label={'Desired Move In Date'}
                placeholder="MM/DD/YYYY"
                isError={!!formik.errors?.DesiredMoveInDate && hasValidationQueryParam}
                errors={formik.errors?.DesiredMoveInDate}
                touched={formik.touched?.DesiredMoveInDate}
                {...formik.getFieldProps('DesiredMoveInDate')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                onChange={(e) => formatDesiredMoveInDate(e.target.value)}
                showAsterisk={!formik.values.DesiredMoveInDate}
              />
              <InputField
                label={'Expected Rent Amount'}
                isError={!!formik.errors?.ExpectedRentAmount && hasValidationQueryParam}
                errors={formik.errors?.ExpectedRentAmount}
                touched={formik.touched?.ExpectedRentAmount}
                showAsterisk={!formik.values.ExpectedRentAmount}
                {...formik.getFieldProps('ExpectedRentAmount')}
                onChange={(e) => {
                  const input = e.target.value;
                  // Remove non-numeric characters and leading zeros
                  const newValue = input.replace(/[^0-9]/g, '').replace(/^0+/, '');
                  formik.setFieldValue('ExpectedRentAmount', newValue !== '' ? parseInt(newValue) : '');
                }}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
              />
              <CustomDropdown
                isFullWidth={true}
                value={formik.values.DesiredLeaseTerm}
                error={hasValidationQueryParam && !formik.values.DesiredLeaseTerm ? 'Please select an option' : undefined}
                label={'Desired Lease Term'}
                showAsterisk={!formik.values.DesiredLeaseTerm}
                handleChange={(e) => {
                  formik.setFieldValue('DesiredLeaseTerm', e.target.value);
                }}
                handleBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                name="DesiredLeaseTerm"
                menuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                    },
                  },
                }}
                customStyle={{
                  marginBottom: '0',
                }}
              >
                {Object.values(ApplicationDesiredLeaseTerm).map((termValue) => (
                  <MenuItem key={termValue} value={termValue}>
                    {termValue}
                  </MenuItem>
                ))}
              </CustomDropdown>
              <Box>
                <Typography className={classes.labelText} sx={{ ml: 1, mt: 3, mb: 1 }}>
                  Add a Note
                </Typography>
                <Box
                  sx={{
                    '& fieldset': {
                      borderRadius: '24px',
                      borderColor: 'white',
                    },
                  }}
                >
                  <TextField
                    multiline
                    rows={4}
                    sx={{ width: '100%', backgroundColor: !!formik.errors?.ApplicantMemo && hasValidationQueryParam ? 'pink' : '#F1F0F5', borderRadius: '24px' }}
                    placeholder={'Description'}
                    error={!!formik.errors?.ApplicantMemo}
                    {...formik.getFieldProps('ApplicantMemo')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    inputProps={{ maxLength: 1000 }}
                  />

                  <Typography
                    className={classes.applicationNoteText}
                    sx={{ textAlign: 'right', color: 1000 - (formik.values.ApplicantMemo?.length ?? 0) < 0 ? '#DB3939 !important' : '#14142B !important' }}
                  >{`${1000 - (formik.values.ApplicantMemo?.length ?? 0)} characters left.`}</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} lg={4.5}>
              <Box mb={2}>
                <Typography className={classes.labelText}>Have you (or any coapplicants) seen the property?</Typography>
                <RadioGroup row onChange={handleUpdateHasSeenProperty} value={hasSeenProperty == null ? null : hasSeenProperty}>
                  <FormControlLabel value={true} control={<Radio size="small" />} sx={hasSeenProperty == null && hasValidationQueryParam ? { color: 'red' } : {}} label="Yes" />
                  <FormControlLabel value={false} control={<Radio size="small" />} sx={hasSeenProperty == null && hasValidationQueryParam ? { color: 'red' } : {}} label="No" />
                </RadioGroup>
              </Box>
              {hasSeenProperty && (
                <Box height="100%" width="100%">
                  <Typography sx={{ mb: 2 }} className={classes.labelText}>
                    Showing Agent Details
                  </Typography>
                  <InputField
                    label={'Showing Agent Name'}
                    isError={!!formik.errors?.ShowingAgentName && hasValidationQueryParam && hasSeenProperty}
                    errors={formik.errors?.ShowingAgentName}
                    touched={formik.touched?.ShowingAgentName}
                    {...formik.getFieldProps('ShowingAgentName')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    showAsterisk={!formik.values.ShowingAgentName}
                  />
                  <InputField
                    label={'Showing Agent Company'}
                    isError={!!formik.errors?.ShowingAgentCompany && hasValidationQueryParam}
                    errors={formik.errors?.ShowingAgentCompany}
                    touched={formik.touched?.ShowingAgentCompany}
                    {...formik.getFieldProps('ShowingAgentCompany')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    showAsterisk={!formik.values.ShowingAgentCompany}
                  />
                  <InputField
                    label={'Showing Agent Contact Details'}
                    isError={!!formik.errors?.ShowingAgentContactDetails && hasValidationQueryParam}
                    errors={formik.errors?.ShowingAgentContactDetails}
                    touched={formik.touched?.ShowingAgentContactDetails}
                    {...formik.getFieldProps('ShowingAgentContactDetails')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    showAsterisk={!formik.values.ShowingAgentContactDetails}
                  />
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}
